import { getApiUrl, tokenInterceptor, paramsSerializer } from '@app/api';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const syncMonitorApi = createApi({
  reducerPath: 'syncMonitorApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getApiUrl('/sync-monitor'),
    prepareHeaders: tokenInterceptor,
    paramsSerializer,
  }),
  tagTypes: ['SyncMonitor'],
  endpoints: (builder) => ({
    syncCalendar: builder.query({
      query: ({ centerIds, from, to }) => ({
        url: '/calendar',
        params: { center_ids: centerIds, from, to },
      }),
      providesTags: ['SyncMonitor'],
    }),
    searchSyncCenters: builder.query({
      query: ({ q }) => ({
        url: '/centers',
        params: { q },
      }),
    }),
    syncDayDetail: builder.query({
      query: ({ centerId, date }) => ({
        url: '/day-detail',
        params: { center_id: centerId, date },
      }),
    }),
    syncHoles: builder.query({
      query: ({ year, month, centerIds, tags, source, type, group, page, limit }) => ({
        url: '/holes',
        params: {
          year,
          ...(month ? { month } : {}),
          ...(centerIds && centerIds.length ? { center_ids: centerIds } : {}),
          ...(tags && tags.length ? { tags } : {}),
          source,
          type,
          group,
          page,
          limit,
        },
      }),
    }),
    syncAnomalies: builder.query({
      query: ({ code, tags, q, page, limit }) => ({
        url: '/anomalies',
        params: {
          ...(code ? { code } : {}),
          ...(tags && tags.length ? { tags } : {}),
          ...(q ? { q } : {}),
          page,
          limit,
        },
      }),
    }),
    // CSV of the holes list; the body is text, so it is read as such.
    exportSyncHoles: builder.query({
      query: ({ year, month, centerIds, tags, source, type }) => ({
        url: '/holes/export',
        params: {
          year,
          ...(month ? { month } : {}),
          ...(centerIds && centerIds.length ? { center_ids: centerIds } : {}),
          ...(tags && tags.length ? { tags } : {}),
          source,
          type,
        },
        responseHandler: (response) => response.text(),
      }),
    }),
    // Live HeadObject on the customer bucket. Lazy: only fired by the "Check live" button.
    syncS3Object: builder.query({
      query: ({ centerId, date, type }) => ({
        url: '/s3-object',
        params: { center_id: centerId, date, type },
      }),
    }),
    // Tag vocabulary and how many suitcases / schools carry each tag (filter chips).
    syncTags: builder.query({
      query: () => ({ url: '/tags' }),
      providesTags: ['SyncMonitor'],
    }),
    syncStatsSummary: builder.query({
      query: ({ centerIds, year, month, tags }) => ({
        url: '/stats-summary',
        params: {
          ...(centerIds && centerIds.length ? { center_ids: centerIds } : {}),
          ...(tags && tags.length ? { tags } : {}),
          ...(year ? { year } : {}),
          ...(month ? { month } : {}),
        },
      }),
      providesTags: ['SyncMonitor'],
    }),
    syncGlobalOverview: builder.query({
      query: ({ tags } = {}) => ({
        url: '/global-overview',
        params: { ...(tags && tags.length ? { tags } : {}) },
      }),
      providesTags: ['SyncMonitor'],
    }),
    syncPeriodTotals: builder.query({
      query: ({ centerIds, year, tags }) => ({
        url: '/period-totals',
        params: {
          year,
          ...(centerIds && centerIds.length ? { center_ids: centerIds } : {}),
          ...(tags && tags.length ? { tags } : {}),
        },
      }),
      providesTags: ['SyncMonitor'],
    }),
    syncGlobalCalendar: builder.query({
      query: ({ year, month, tags }) => ({
        url: '/global-calendar',
        params: {
          year,
          ...(month ? { month } : {}),
          ...(tags && tags.length ? { tags } : {}),
        },
      }),
      providesTags: ['SyncMonitor'],
    }),
    syncRecentByStatus: builder.query({
      query: ({ status, centerIds, year, month, date, limit, page, q, tags }) => ({
        url: '/recent-by-status',
        params: {
          status,
          ...(centerIds && centerIds.length ? { center_ids: centerIds } : {}),
          ...(tags && tags.length ? { tags } : {}),
          ...(date ? { date } : {}),
          ...(year && !date ? { year } : {}),
          ...(month && !date ? { month } : {}),
          ...(limit ? { limit } : {}),
          ...(page ? { page } : {}),
          ...(q ? { q } : {}),
        },
      }),
    }),
  }),
});

export const {
  useSyncCalendarQuery,
  useSearchSyncCentersQuery,
  useLazySearchSyncCentersQuery,
  useSyncDayDetailQuery,
  useLazySyncS3ObjectQuery,
  useSyncHolesQuery,
  useSyncAnomaliesQuery,
  useLazyExportSyncHolesQuery,
  useSyncStatsSummaryQuery,
  useSyncRecentByStatusQuery,
  useSyncGlobalCalendarQuery,
  useSyncGlobalOverviewQuery,
  useSyncPeriodTotalsQuery,
  useSyncTagsQuery,
} = syncMonitorApi;
